import type { DeliveryGroup as CartDeliveryGroup } from '@/modules/cart/domain/Cart';
import {
  type Cart,
  type DeliveryGroup as DeliveryGroupCart,
  EShippingMethod,
  EShippingSubType,
  EShippingType
} from '@/modules/cart/domain/Cart';
import { type CartLineItem } from '@/modules/cart/domain/CartLineItem';
import type { CartProductOption } from '@/modules/cart/domain/CartProductOption';
import {
  isDeviceAndroid,
  isDeviceIos
} from '@/modules/shared/utils/deviceDetections';
import { getProductBrand } from '@/modules/shared/utils/getProductBrand';
import { getProductPrice } from '@/modules/shared/utils/getProductPrice';
import { SHIPPING_ACTION_FIELD } from '@/modules/shared/utils/gtmEvents/events/constants';
import { createGTMProductWebViewObject } from '@/modules/shared/utils/gtmEvents/events/webViewEvents';
import { sendEvent } from '@/modules/shared/utils/gtmEvents/sendEvents/sendShippingEvents';
import type {
  GTMEcommerceCheckout,
  GtmEcommerceProducts,
  IWebViewGtmProduct
} from '@/modules/shared/utils/gtmEvents/types';
import {
  EAnalyticsShippingMethodType,
  EEventName,
  EWebViewNameEvent,
  type GtmEventProductPerfomance,
  type IAnalyticsShippingDevice
} from '@/modules/shared/utils/gtmEvents/types';
import type { DeliveryGroup } from '@/modules/shippingMethod/domain/ShippingMethod';

const isWebView = isDeviceAndroid() || isDeviceIos();

type AttributeValue =
  | string
  | string[]
  | { key: string; label: string }
  | boolean;

const getAttributeAsStringOrObject = (
  value: AttributeValue
): string | { key: string; label: string } | undefined => {
  if (typeof value === 'string') return value;
  if (typeof value === 'boolean') return value.toString();
  if (Array.isArray(value) && value.length > 0) return String(value[0]);
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    'key' in value &&
    'label' in value
  ) {
    return value;
  }
  return undefined;
};

const getProductPrices = (product: CartLineItem) => {
  const { paymentMethod, regular } = product?.variant?.prices || {};
  const basePrice = regular?.value?.centAmount ?? 0;
  const offerPrice = paymentMethod?.value?.centAmount ?? 0;

  return {
    basePrice,
    offerPrice
  };
};

const getDimension17 = (sku: string, deliveryGroups?: DeliveryGroupCart[]) => {
  const productShippingMethodGroup = deliveryGroups?.find((group) =>
    group?.products?.map((productId) => productId).includes(sku)
  );
  const option = productShippingMethodGroup?.shippingOption;

  if (!option) return '';

  const shippingType = option?.shippingType;
  const shippingMethod = option?.shippingMethod;

  const isCollect = shippingType === EShippingType.COLLECT;
  const isDelivery = shippingType === EShippingType.DELIVERY;

  if (isCollect) return EAnalyticsShippingMethodType.COLLECT;
  if (isDelivery) {
    const isDateRange = shippingMethod === EShippingMethod.DATE_RANGE;
    const isExpress = shippingMethod === EShippingMethod.NEXT_DAY;
    const isScheduled = shippingMethod === EShippingMethod.SCHEDULED;

    if (isScheduled) return EAnalyticsShippingMethodType.SCHEDULED;
    if (isDateRange) return EAnalyticsShippingMethodType.DATE_RANGE;
    if (isExpress) return EAnalyticsShippingMethodType.NEXT_DAY;
  }

  return '';
};

export const getEcommerceProductsCheckout = (
  cart: Cart
): GtmEcommerceProducts[] => {
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups;

  return cart.lineItems.map((product) => {
    const sku = product?.variant?.sku ?? '';
    const attributes = Object.fromEntries(
      product?.variant?.attributes?.map((attr) => [attr.name, attr.value]) || []
    );

    const dimension17 = getDimension17(sku, deliveryGroups);
    const { basePrice, offerPrice } = getProductPrices(product);

    return {
      id: sku,
      name: product.name || '',
      variant: sku,
      category: product.productType?.key || '',
      brand: getProductBrand(product) || '',
      price: getProductPrice(product),
      dimension2: getAttributeAsStringOrObject(attributes['departmentId']),
      dimension3: getAttributeAsStringOrObject(attributes['departmentName']),
      dimension17: dimension17,
      dimension32: getAttributeAsStringOrObject(attributes['classId']),
      dimension11: getAttributeAsStringOrObject(attributes['className']),
      dimension33: getAttributeAsStringOrObject(attributes['subdepartmentId']),
      dimension12: getAttributeAsStringOrObject(
        attributes['subdepartmentName']
      ),
      dimension19: basePrice || 0,
      dimension20: offerPrice || 0,
      dimension21: product.variant.warranties || false,
      dimension30: product.seller || '',
      dimension76: attributes['sponsored'] || false,
      quantity: product.quantity || 0
    };
  });
};

export const getProductMapEvent = (product: CartLineItem) => {
  const attributes = Object.fromEntries(
    product.variant.attributes?.map((attr) => [attr.name, attr.value]) || []
  );

  const shippingOptions = product.availableShippingOptions?.map(
    (option) => option.label
  );

  const { basePrice, offerPrice } = getProductPrices(product);

  return {
    id: product.variant.sku || '',
    name: product.name || '',
    variant: product.variant.sku || '',
    category: product.productType?.key || '',
    brand: getProductBrand(product) || '',
    price: getProductPrice(product),
    dimension2: getAttributeAsStringOrObject(attributes['departmentId']),
    dimension3: getAttributeAsStringOrObject(attributes['departmentName']),
    dimension17: shippingOptions.join(', ') || '',
    dimension32: getAttributeAsStringOrObject(attributes['classId']),
    dimension11: getAttributeAsStringOrObject(attributes['className']), //unmapped
    dimension33: attributes['subdepartmentId'] || '',
    dimension12: attributes['subdepartmentName'] || '', //unmapped
    dimension19: basePrice || 0, //basePrice
    dimension20: offerPrice || 0, //offerPrice
    dimension21: product.variant.warranties || false,
    dimension30: product.seller || '',
    dimension76: attributes['sponsored'] || false,
    quantity: product.quantity || 0
  };
};

export const getProductMapForAddToCartEvent = (
  product: CartLineItem,
  quantity: number,
  productOptions: CartProductOption[]
): GtmEcommerceProducts => {
  const attributes = Object.fromEntries(
    product?.variant?.attributes?.map((attr) => [attr.name, attr.value]) || []
  );

  const shippingOptions = product?.availableShippingOptions?.map(
    (option) => option.label
  );

  const productoOption = productOptions.find(
    (option) => option?.productSku === product?.variant?.sku
  );

  const { basePrice, offerPrice } = getProductPrices(product);

  return {
    id: product?.variant?.sku || '',
    name: product?.name || '',
    variant: product?.variant?.sku || '',
    category: product?.productType?.key || '',
    brand: getProductBrand(product) || '',
    price: getProductPrice(product),
    dimension2: getAttributeAsStringOrObject(attributes['departmentId']) || '',
    dimension3:
      getAttributeAsStringOrObject(attributes['departmentName']) || '', //unmapped
    dimension17: shippingOptions.join(', ') || '',
    dimension32: getAttributeAsStringOrObject(attributes['classId']),
    dimension11: getAttributeAsStringOrObject(attributes['className']), //unmapped
    dimension33:
      getAttributeAsStringOrObject(attributes['subdepartmentId']) || '',
    dimension12:
      getAttributeAsStringOrObject(attributes['subdepartmentName']) || '', //unmapped
    dimension18: getAttributeAsStringOrObject(attributes['size']) || '',
    dimension19: basePrice || 0, //basePrice
    dimension20: offerPrice || 0, //offerPrice
    dimension21: product?.variant?.warranties || false,
    dimension30: product?.seller || '',
    dimension36: productoOption?.name,
    dimension41: 0,
    dimension42: 0,
    metric9: productoOption?.totalPrice?.centAmount,
    quantity
  };
};
export const getDeliveryGroupsLength = (
  deliveryGroups: DeliveryGroup[] | undefined
) => {
  return deliveryGroups?.length || 0;
};

export const getProductTotalPrice = (
  products: Cart['lineItems'],
  productOptions: Cart['productOptions']
) => {
  return products?.reduce((total, product) => {
    const basePrice = product?.variant?.prices?.regular?.value?.centAmount ?? 0;
    const optionsTotal = productOptions
      ?.filter((o) => o.productSku === product?.variant?.sku)
      .reduce((sum, option) => sum + (option.totalPrice?.centAmount ?? 0), 0);
    return total + basePrice + optionsTotal;
  }, 0);
};

export const getLeadTimes = (group: CartDeliveryGroup) => {
  const deliveryDateTo =
    group?.optionSelected?.deliveryDateTo ??
    group?.optionSelected?.deliveryDateTo ??
    '';
  const deliveryDate = deliveryDateTo?.split('T')[0] ?? '';
  const shippingType = group?.shippingOption?.shippingType;
  const shippingSubType =
    group?.optionSelected?.shippingSubType ??
    group?.shippingOption?.shippingSubType;
  const isCollect = shippingType === EShippingType.COLLECT;
  const isCopec = shippingSubType === EShippingSubType.COPEC;
  const isInStore = shippingSubType === EShippingSubType.IN_STORE;
  const isDelivery = shippingType === EShippingType.DELIVERY;

  const formatDate = (dateStr: string): string =>
    dateStr ? dateStr.replaceAll('-', '/') : '';
  const leadTimeDespacho = isDelivery ? formatDate(deliveryDate) : '';
  const leadTimeRetiro = isCollect && isInStore ? formatDate(deliveryDate) : '';
  const leadTimePuntosCopec =
    isCollect && isCopec ? formatDate(deliveryDate) : '';
  return {
    leadTimeDespacho,
    leadTimeRetiro,
    leadTimePuntosCopec
  };
};

export const getCheckoutParams = (cart: Cart): IAnalyticsShippingDevice => {
  return {
    AnalyticsParameterCurrency: cart?.totalPrice?.currencyCode ?? 'CLP',
    AnalyticsParameterValue: cart?.totalPrice?.centAmount ?? 0,
    AnalyticsParameterCoupon: '',
    AnalyticsParameterShippingTier: ''
  };
};

export const getEcommerceCheckout = (cart: Cart): GTMEcommerceCheckout => {
  if (isWebView) {
    const productList = createGTMProductWebViewObject(cart);
    return {
      checkout: {
        actionField: SHIPPING_ACTION_FIELD,
        products: productList,
        checkoutParams: getCheckoutParams(cart),
        documentType: cart?.shippingAddress?.documentType ?? 'RUT'
      }
    };
  }

  return {
    checkout: {
      actionField: SHIPPING_ACTION_FIELD,
      products: getEcommerceProductsCheckout(cart),
      documentType: cart?.shippingAddress?.documentType ?? 'RUT'
    }
  };
};

export const getProductPerformanceEvent = (
  cart: Cart
): GtmEventProductPerfomance => {
  const ecommerce = getEcommerceCheckout(cart);

  return {
    event: EEventName.CHECKOUT,
    'type-User': cart?.customerId ? 'authenticated' : 'guest',
    ecommerce: ecommerce
  };
};

export const sendTypeDeliveryPageViewGTMEvent = (cart: Cart): void => {
  const productPerformanceObject = getProductPerformanceEvent(cart);

  if (isWebView) {
    sendEvent({
      event: EWebViewNameEvent.ACTION_CHECKOUT_ADD_INFO,
      extraFields: productPerformanceObject
    });
  }

  sendEvent({
    event: EWebViewNameEvent.ACTION_PRE_CHECKOUT_APPLIANCE,
    extraFields: productPerformanceObject
  });
};

export const getEcommerceFromGroup = (
  ecommerce: GTMEcommerceCheckout,
  group: CartDeliveryGroup
): GTMEcommerceCheckout => {
  const productsInGroup = ecommerce?.checkout?.products?.filter((product) => {
    const sku =
      'id' in product
        ? product.id
        : (product as IWebViewGtmProduct).AnalyticsParameterItemID;

    if (!sku) return false;

    return group?.products?.includes(sku);
  });

  return {
    checkout: {
      actionField: ecommerce?.checkout?.actionField,
      products: productsInGroup as GtmEcommerceProducts[] | IWebViewGtmProduct[]
    }
  };
};
